<script>
    import { createEventDispatcher } from 'svelte';
    
    const dispatch = createEventDispatcher();
    
    export let startTime = "";
    export let endTime = "";
    export let dayOfWeek = "1";
    
    const dayNames = {
        "1": "Monday",
        "2": "Tuesday",
        "3": "Wednesday",
        "4": "Thursday",
        "5": "Friday",
        "6": "Saturday",
        "7": "Sunday"
    };

    let isDragging = false;
    let dragType = null; // 'start' or 'end'
    let timelineElement;
    let timelineRect = { left: 0, width: 0 };

    // Convert time string (HH:MM) to minutes since midnight
    function timeToMinutes(timeStr) {
        if (!timeStr) return 0;
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
    }

    // Convert minutes to time string
    function minutesToTime(minutes) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    }

    // Convert time to percentage position on timeline (0-100%)
    function timeToPosition(timeStr) {
        const minutes = timeToMinutes(timeStr);
        // Assuming 24-hour timeline (0-1440 minutes)
        return (minutes / 1440) * 100;
    }

    // Convert position percentage to time
    function positionToTime(position) {
        const minutes = Math.round((position / 100) * 1440);
        // Round to nearest 15 minutes
        const rounded = Math.round(minutes / 15) * 15;
        return minutesToTime(Math.max(0, Math.min(1440, rounded)));
    }

    function updateTimelineRect() {
        if (timelineElement) {
            const rect = timelineElement.getBoundingClientRect();
            timelineRect = {
                left: rect.left,
                width: rect.width
            };
        }
    }

    function handleMouseDown(e, type) {
        e.preventDefault();
        isDragging = true;
        dragType = type;
        updateTimelineRect();
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }

    function handleMouseMove(e) {
        if (!isDragging || !timelineElement) return;
        
        const x = e.clientX - timelineRect.left;
        const position = Math.max(0, Math.min(100, (x / timelineRect.width) * 100));
        const newTime = positionToTime(position);

        if (dragType === 'start') {
            const endMinutes = timeToMinutes(endTime || "23:59");
            const newMinutes = timeToMinutes(newTime);
            if (newMinutes < endMinutes) {
                startTime = newTime;
                dispatch('startTimeChange', newTime);
            }
        } else if (dragType === 'end') {
            const startMinutes = timeToMinutes(startTime || "00:00");
            const newMinutes = timeToMinutes(newTime);
            if (newMinutes > startMinutes) {
                endTime = newTime;
                dispatch('endTimeChange', newTime);
            }
        }
    }

    function handleMouseUp() {
        isDragging = false;
        dragType = null;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    }

    function handleTimelineClick(e) {
        if (isDragging) return;
        updateTimelineRect();
        const x = e.clientX - timelineRect.left;
        const position = Math.max(0, Math.min(100, (x / timelineRect.width) * 100));
        const clickedTime = positionToTime(position);
        
        const startMinutes = timeToMinutes(startTime || "00:00");
        const endMinutes = timeToMinutes(endTime || "23:59");
        const clickedMinutes = timeToMinutes(clickedTime);

        // Determine which handle is closer
        const distToStart = Math.abs(clickedMinutes - startMinutes);
        const distToEnd = Math.abs(clickedMinutes - endMinutes);

        if (distToStart < distToEnd) {
            if (clickedMinutes < endMinutes) {
                startTime = clickedTime;
                dispatch('startTimeChange', clickedTime);
            }
        } else {
            if (clickedMinutes > startMinutes) {
                endTime = clickedTime;
                dispatch('endTimeChange', clickedTime);
            }
        }
    }

    function snapToNearest15(timeStr) {
        const minutes = timeToMinutes(timeStr);
        const rounded = Math.round(minutes / 15) * 15;
        return minutesToTime(Math.max(0, Math.min(1440, rounded)));
    }

    $: startPos = timeToPosition(startTime || "00:00");
    $: endPos = timeToPosition(endTime || "23:59");
    $: rangeWidth = Math.max(2, endPos - startPos);
</script>

<div class="time-range-picker">
    <div class="picker-header">
        <select 
            bind:value={dayOfWeek} 
            class="day-select"
            on:change={(e) => dispatch('dayOfWeekChange', e.target.value)}
        >
            {#each Object.entries(dayNames) as [value, name]}
                <option value={value}>{name}</option>
            {/each}
        </select>
        <div class="time-display">
            <span>{startTime || "00:00"}</span>
            <span class="separator">→</span>
            <span>{endTime || "23:59"}</span>
        </div>
    </div>

    <div class="timeline-container">
        <div 
            class="timeline"
            bind:this={timelineElement}
            on:click={handleTimelineClick}
            role="slider"
            aria-label="Time range selector"
        >
            <div class="timeline-background"></div>
            <div 
                class="time-range"
                style="left: {startPos}%; width: {rangeWidth}%"
            ></div>
            <div 
                class="handle handle-start"
                style="left: {startPos}%"
                on:mousedown={(e) => handleMouseDown(e, 'start')}
                role="button"
                aria-label="Start time"
            ></div>
            <div 
                class="handle handle-end"
                style="left: {endPos}%"
                on:mousedown={(e) => handleMouseDown(e, 'end')}
                role="button"
                aria-label="End time"
            ></div>
        </div>
        
        <div class="time-labels">
            <span>00:00</span>
            <span>06:00</span>
            <span>12:00</span>
            <span>18:00</span>
            <span>23:59</span>
        </div>
    </div>

    <div class="time-inputs">
        <div class="time-input-group">
            <label>Start Time</label>
            <input 
                type="time" 
                value={startTime}
                on:change={(e) => {
                    const newTime = snapToNearest15(e.target.value);
                    if (timeToMinutes(newTime) < timeToMinutes(endTime || "23:59")) {
                        startTime = newTime;
                        dispatch('startTimeChange', newTime);
                    } else {
                        e.target.value = startTime; // Reset if invalid
                    }
                }}
            />
        </div>
        <div class="time-input-group">
            <label>End Time</label>
            <input 
                type="time" 
                value={endTime}
                on:change={(e) => {
                    const newTime = snapToNearest15(e.target.value);
                    if (timeToMinutes(newTime) > timeToMinutes(startTime || "00:00")) {
                        endTime = newTime;
                        dispatch('endTimeChange', newTime);
                    } else {
                        e.target.value = endTime; // Reset if invalid
                    }
                }}
            />
        </div>
    </div>
</div>

<style>
    .time-range-picker {
        background: white;
        border: 2px solid #e5e7eb;
        border-radius: 10px;
        padding: 1.5em;
        margin-bottom: 1em;
    }

    .picker-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.5em;
        flex-wrap: wrap;
        gap: 1em;
    }

    .day-select {
        padding: 0.5em 1em;
        border: 2px solid #e5e7eb;
        border-radius: 6px;
        font-size: 1em;
        background: white;
        cursor: pointer;
    }

    .time-display {
        display: flex;
        align-items: center;
        gap: 0.5em;
        font-size: 1.1em;
        font-weight: 600;
        color: #111827;
    }

    .separator {
        color: #6b7280;
    }

    .timeline-container {
        margin-bottom: 1em;
    }

    .timeline {
        position: relative;
        height: 60px;
        cursor: pointer;
        margin-bottom: 0.5em;
    }

    .timeline-background {
        position: absolute;
        top: 50%;
        left: 0;
        right: 0;
        height: 8px;
        background: #e5e7eb;
        border-radius: 4px;
        transform: translateY(-50%);
    }

    .time-range {
        position: absolute;
        top: 50%;
        height: 8px;
        background: #fab45a;
        border-radius: 4px;
        transform: translateY(-50%);
        pointer-events: none;
    }

    .handle {
        position: absolute;
        top: 50%;
        width: 20px;
        height: 20px;
        background: #fab45a;
        border: 3px solid white;
        border-radius: 50%;
        transform: translate(-50%, -50%);
        cursor: grab;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        transition: transform 0.1s ease;
    }

    .handle:active {
        cursor: grabbing;
        transform: translate(-50%, -50%) scale(1.2);
    }

    .handle:hover {
        background: #f9a742;
    }

    .time-labels {
        display: flex;
        justify-content: space-between;
        font-size: 0.75em;
        color: #6b7280;
        padding: 0 10px;
    }

    .time-inputs {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1em;
    }

    .time-input-group {
        display: flex;
        flex-direction: column;
    }

    .time-input-group label {
        font-size: 0.875em;
        font-weight: 600;
        color: #374151;
        margin-bottom: 0.5em;
    }

    .time-input-group input[type="time"] {
        padding: 0.75em;
        border: 2px solid #e5e7eb;
        border-radius: 6px;
        font-size: 1em;
        transition: all 0.2s ease;
    }

    .time-input-group input[type="time"]:focus {
        outline: none;
        border-color: #fab45a;
        box-shadow: 0 0 0 3px rgba(250, 180, 90, 0.1);
    }

    @media (max-width: 768px) {
        .time-inputs {
            grid-template-columns: 1fr;
        }
    }
</style>

